import React from 'react';

/**
 * Mounts (or suppresses) the Gorgias customer-support chat widget.
 *
 * The widget is for customers. Staff tooling — the inventory manager, scanner,
 * ship station — is operated at a workstation where a floating chat bubble sits
 * on top of the controls, so staff sessions must never show it.
 *
 * Suppression has to be able to happen LATE: `user` hydrates asynchronously, so
 * the first render of the shell often has no permission information. If the
 * widget was already injected by the time we learn the viewer is staff, this
 * tears it back down rather than leaving it on screen.
 */

const SCRIPT_ID = 'gorgias-chat-widget-install-v3';
const CONTAINER_ID = 'gorgias-chat-container';
const HIDE_STYLE_ID = 'wm-hide-gorgias-chat';
const BUNDLE_URL = 'https://config.gorgias.chat/bundle-loader/01JJ2XJQ24DFEB96SXJ2YVXQ21';

// Gorgias renders several sibling nodes, not just the container, and re-creates
// them on its own schedule — so suppression is a CSS rule plus node removal
// rather than removal alone.
const HIDE_CSS = `#${CONTAINER_ID},.gorgias-chat-container,.gorgias-chat-key-placeholder{display:none!important;visibility:hidden!important;opacity:0!important;pointer-events:none!important;}`;

// #region removeGorgiasNodes
const removeGorgiasNodes = (): void => {
    const targets = [
        document.getElementById(SCRIPT_ID),
        document.getElementById(CONTAINER_ID),
        ...Array.from(document.querySelectorAll('.gorgias-chat-container'))
    ];

    targets.forEach((node) => {
        // Best effort: Gorgias may already have detached a node itself.
        try {
            node?.parentNode?.removeChild(node);
        } catch (e) {
            console.warn('Failed to remove Gorgias node', e);
        }
    });
};

// #region useGorgiasChat
export const useGorgiasChat = (suppressed: boolean): void => {
    React.useEffect(() => {
        if (typeof document === 'undefined') return;

        if (suppressed) {
            // The stylesheet stays for the lifetime of the suppression so that
            // anything Gorgias re-injects asynchronously is hidden too.
            let style = document.getElementById(HIDE_STYLE_ID);
            if (!style) {
                style = document.createElement('style');
                style.id = HIDE_STYLE_ID;
                style.textContent = HIDE_CSS;
                document.head.appendChild(style);
            }

            removeGorgiasNodes();

            return () => {
                try {
                    style?.parentNode?.removeChild(style);
                } catch (e) {
                    console.warn('Failed to remove Gorgias hide style', e);
                }
            };
        }

        if (document.getElementById(CONTAINER_ID) || document.getElementById(SCRIPT_ID)) return;

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = BUNDLE_URL;
        script.async = true;
        document.body.appendChild(script);

        // The container only exists once the bundle has booted, hence the delay.
        const timer = setTimeout(() => {
            const chat = document.getElementById(CONTAINER_ID);
            if (chat) {
                chat.style.position = 'relative';
                chat.style.zIndex = '0';
            }
        }, 1000);

        return () => {
            clearTimeout(timer);
            try {
                script.parentNode?.removeChild(script);
            } catch (e) {
                console.warn('Failed to remove Gorgias script', e);
            }
        };
    }, [suppressed]);
};
